import moment, { Moment } from 'moment'
import { DatePicker } from 'antd'
import Card from 'antd/lib/card/Card'
import { useEffect, useMemo, useState } from 'react'
import { BarChart } from './BarChart'
import { getIncomeEvent, getRevenueEvent, RootState, useAppDispatch } from '@redux'
import { useSelector } from 'react-redux'
import { RangeValue } from 'src/pages/dashboard-teacher'

const { RangePicker } = DatePicker

const DEFAULT_VALUE: [Moment, Moment] = [moment().startOf('year'), moment()]

export const EventDashboardPage = () => {
  const [incomeRange, setIncomeRange] = useState<[Moment, Moment] | null>(null)
  const [revenueRange, setRevenueRange] = useState<[Moment, Moment] | null>(null)

  const dispatch = useAppDispatch()
  const { dataRevenuEvent, dataIncomeEvent } = useSelector(
    (state: RootState) => state.eventPosts
  )

  const handleIncomeCalendarChange = (
    dates: RangeValue<Moment>,
    _formatString: [string, string],
    _info: any
  ) => {
    if (!dates) {
      setIncomeRange(null)
      return
    }
    if (dates && (dates[0] || dates[1])) {
      setIncomeRange([
        dates[0] ?? DEFAULT_VALUE[0],
        dates[1] ?? DEFAULT_VALUE[1],
      ])
    }
  }

  const handleRevenueCalendarChange = (
    dates: RangeValue<Moment>,
    _formatString: [string, string],
    _info: any
  ) => {
    if (!dates) {
      setRevenueRange(null)
      return
    }

    if (dates && (dates[0] || dates[1])) {
      setRevenueRange([
        dates[0] ?? DEFAULT_VALUE[0],
        dates[1] ?? DEFAULT_VALUE[1],
      ])
    }
  }

  const getDisabledDate =
    (start: Moment | null) =>
      (current: Moment | null): boolean => {
        if (!start || !current) return false
        const diff = current.diff(start, 'month')
        return Math.abs(diff) > 10
      }

  // --- FETCH DATA ---
  useEffect(() => {
    setIncomeRange(DEFAULT_VALUE)
    setRevenueRange(DEFAULT_VALUE)
  }, [])

  useEffect(() => {
    if (revenueRange) {
      dispatch(
        getRevenueEvent({
          startDate: revenueRange[0].format('YYYY-MM-DD'),
          endDate: revenueRange[1].format('YYYY-MM-DD'),
        })
      )
    }

  }, [dispatch, revenueRange])

  useEffect(() => {
    if (incomeRange) {
      dispatch(
        getIncomeEvent({
          startDate: incomeRange[0].format('YYYY-MM-DD'),
          endDate: incomeRange[1].format('YYYY-MM-DD'),
        })
      )
    }
  }, [dispatch, incomeRange])

  // --- CALCULATE TOTALS ---
  const totalIncomeOfMonths = useMemo(
    () => dataIncomeEvent?.income.reduce((acc, cur) => acc + cur.value, 0) ?? 0,
    [dataIncomeEvent]
  )

  const totalRevenueOfMonths = useMemo(
    () => dataRevenuEvent?.revenue.reduce((acc, cur) => acc + cur.value, 0) ?? 0,
    [dataRevenuEvent]
  )

  return (
    <Card>
      {/* ---------- INCOME ---------- */}
      <div>
        <h2 className="font-bold text-3xl">Income</h2>
        <div className="rounded-[10px] shadow-xl p-5 text-dark w-fit min-w-[300px] border-gray-200 border">
          <p className="font-bold">Total Income</p>
          <p className="text-3xl font-bold">{dataIncomeEvent?.currency === 'EUR' ? '€' : '$'}{totalIncomeOfMonths}</p>
          <p>Total income of month(s)</p>
        </div>

        <div className="flex gap-x-4 items-center my-10">
          <RangePicker
            picker="month"
            allowClear
            className="h-[40px]"
            value={incomeRange}
            onCalendarChange={handleIncomeCalendarChange}
            disabledDate={getDisabledDate(incomeRange && incomeRange[0])}
            format="YYYY-MM"
          />
        </div>

        <BarChart data={dataIncomeEvent?.income ?? []} dataDonutChart={dataIncomeEvent?.packages} />
      </div>

      {/* ---------- REVENUE ---------- */}
      <div className="mt-10">
        <h2 className="font-bold text-3xl">Revenue</h2>
        <div className="rounded-[10px] shadow-xl p-5 text-dark w-fit min-w-[300px] border-gray-200 border">
          <p className="font-bold">Total Revenue</p>
          <p className="text-3xl font-bold">{dataIncomeEvent?.currency === 'EUR' ? '€' : '$'}{totalRevenueOfMonths}</p>
          <p>Total revenue of month(s)</p>
        </div>

        <div className="flex gap-x-4 items-center my-10">
          <RangePicker
            picker="month"
            allowClear
            className="h-[40px]"
            value={revenueRange}
            onCalendarChange={handleRevenueCalendarChange}
            disabledDate={getDisabledDate(revenueRange && revenueRange[0])}
            format="YYYY-MM"
          />
        </div>

        <BarChart data={dataRevenuEvent?.revenue ?? []} dataDonutChart={dataRevenuEvent?.packages} />
      </div>
    </Card>
  )
}
